import { ContactEmailRequest, ContactEmailResponse } from '../../../types/contactEmail';

export async function sendContactEmail(request: ContactEmailRequest): Promise<ContactEmailResponse> {
  const { postRequest } = await import('../../../services/apiService');
  const response = await postRequest('/api/v1/contact/send', request);
  if (!response) throw new Error('No response from contact email API');
  return response as ContactEmailResponse;
}

export async function sendContactBroadcast(request: ContactEmailRequest): Promise<ContactEmailResponse> {
  const { postRequest } = await import('../../../services/apiService');
  const response = await postRequest('/api/v1/contact/broadcast', request);
  if (!response) throw new Error('No response from contact broadcast API');
  return response as ContactEmailResponse;
}

export type UserSearchResult = {
  id: number;
  email: string;
  handle: string;
  firstname: string;
  role: string;
};

export async function searchUsers(query: string): Promise<UserSearchResult[]> {
  const { getRequest } = await import('../../../services/apiService');
  const response = await getRequest(`/api/v1/contact/search-users?q=${encodeURIComponent(query)}`);
  return response?.data ?? [];
}

export type Campaign = {
  id: number;
  subject: string;
  /** Raw body text the admin typed. Surfaced so the admin UI can render a faithful HTML preview without a second fetch. */
  bodyText?: string;
  /** Optional inbox-preview text shown next to the subject in mail clients. */
  preheader?: string | null;
  ctaText: string | null;
  ctaPath: string | null;
  /** Resolved campaign From header (e.g. "Marshall at BookieBrawler <marshall@news.brawlermail.com>"). */
  fromAddress?: string | null;
  status: 'DRAFT' | 'ACTIVE' | 'PAUSED' | 'COMPLETED' | 'CANCELLED';
  /**
   * Snapshot of the global daily limit at the moment this campaign was
   * created. The scheduler IGNORES this and uses the live
   * EmailSendSettings instead — UIs should treat it as historical
   * context and link to /admin/email-settings for the currently-active
   * pacing.
   */
  dailyLimit: number;
  /** "snapshot-at-create" — backend hint that dailyLimit is not authoritative. */
  dailyLimitSource?: string;
  segmentKey?: string | null;
  growthFindingId?: string | null;
  totalSent: number;
  totalFailed: number;
  totalSkipped: number;
  totalRecipients: number;
  deliveredCount?: number;
  openedCount?: number;
  clickedCount?: number;
  bouncedCount?: number;
  complainedCount?: number;
  delayedCount?: number;
  engagementFailedCount?: number;
  missingResendIds?: number;
  createdAt: string | null;
  lastBatchAt: string | null;
  completedAt: string | null;
  recipientBreakdown?: Record<string, number>;
  sources?: CampaignSource[];
};

export type CampaignSource = {
  id?: number;
  type: 'SEGMENT' | 'CUSTOM_LIST' | 'USER';
  segmentKey?: string | null;
  userId?: number | null;
  email?: string | null;
  handle?: string | null;
  firstname?: string | null;
  createdAt?: string | null;
};

export type CampaignDraftBody = {
  subject: string;
  bodyText: string;
  ctaText?: string;
  ctaPath?: string;
  preheader?: string;
  fromAddress?: string;
  sources: CampaignSource[];
};

export type UserSegmentMembership = {
  key: string;
  label: string;
  description: string;
  recipientCount: number;
  custom?: boolean;
  id?: number;
  rawKey?: string;
  createdAt?: string | null;
};

export type CampaignAudiencePreview = {
  totalRecipients: number;
  emails: string[];
  sourceCounts: Record<string, number>;
  byTypeBreakdown: Record<string, number>;
};

export type CampaignRecipient = {
  id: number;
  userId: number;
  email: string;
  status: 'PENDING' | 'SENT' | 'FAILED' | 'SKIPPED';
  failureReason: string | null;
  sentAt: string | null;
  resendEmailId?: string | null;
  deliveredAt?: string | null;
  openedAt?: string | null;
  firstClickedAt?: string | null;
  lastClickedAt?: string | null;
  bouncedAt?: string | null;
  complainedAt?: string | null;
  delayedAt?: string | null;
  failedAt?: string | null;
  openCount?: number;
  clickCount?: number;
  lastEvent?: string | null;
};

export type CampaignEngagementBackfillResponse = {
  campaignId: number;
  pagesScanned: number;
  emailsScanned: number;
  matched: number;
  missingBefore: number;
  missingAfter: number;
  note?: string;
};

export async function createCampaign(data: {
  subject: string;
  bodyText: string;
  ctaText?: string;
  ctaPath?: string;
  preheader?: string;
  fromAddress?: string;
}): Promise<Campaign> {
  const { postRequest } = await import('../../../services/apiService');
  const response = await postRequest('/api/v1/contact/campaign', data);
  if (!response) throw new Error('No response from campaign API');
  return response as Campaign;
}

/**
 * Pacing context returned alongside the campaigns list. All pacing is
 * configured globally via /admin/email-settings; this response simply
 * mirrors the live values so the UI can render an honest "what's
 * actually being sent today?" without making a second request.
 */
export type CampaignListResponse = {
  campaigns: Campaign[];
  /** Steady-state daily target the admin chose. */
  globalDailyLimit: number;
  /** Today's effective limit accounting for warm-up ramp; <= globalDailyLimit. */
  effectiveDailyLimit?: number;
  activeCampaignCount: number;
  /** Effective limit divided across active campaigns, honoring per-campaign floor. */
  effectivePerCampaign: number;
  perCampaignFloor?: number;
  warmupEnabled?: boolean;
  warmupDaysIn?: number;
  warmupRampDays?: number;
  batchSpreadMinutes?: number;
};

export async function listCampaigns(status?: 'ALL' | Campaign['status']): Promise<CampaignListResponse> {
  const { getRequest } = await import('../../../services/apiService');
  const qs = status && status !== 'ALL' ? `?status=${encodeURIComponent(status)}` : '';
  const response = await getRequest(`/api/v1/contact/campaigns${qs}`);
  if (!response) {
    throw new Error('Failed to load campaigns. Make sure you are signed in as an admin and the local API is running.');
  }
  const data = response?.data ?? response;
  if (Array.isArray(data)) {
    return {
      campaigns: data,
      globalDailyLimit: 200,
      effectiveDailyLimit: 200,
      activeCampaignCount: 0,
      effectivePerCampaign: 200,
    };
  }
  return data as CampaignListResponse;
}

export async function createCampaignDraft(data: CampaignDraftBody): Promise<Campaign> {
  const { postRequest } = await import('../../../services/apiService');
  const response = await postRequest('/api/v1/contact/campaign-draft', data);
  if (!response) throw new Error('No response from campaign draft API');
  return response as Campaign;
}

export async function updateCampaignDraft(id: number, data: CampaignDraftBody): Promise<Campaign> {
  const { apiClient } = await import('../../../services/apiService');
  const response = await apiClient.put(`/api/v1/contact/campaign/${id}`, data);
  return response.data as Campaign;
}

export async function launchCampaignDraft(id: number): Promise<Campaign> {
  const { postRequest } = await import('../../../services/apiService');
  const response = await postRequest(`/api/v1/contact/campaign/${id}/launch`, {});
  if (!response) throw new Error('No response from campaign launch API');
  return response as Campaign;
}

export async function previewCampaignAudience(
  body: Pick<CampaignDraftBody, 'sources'>,
  sample = 20,
): Promise<CampaignAudiencePreview> {
  const { postRequest } = await import('../../../services/apiService');
  const response = await postRequest(`/api/v1/contact/campaign-draft/preview?sample=${sample}`, body);
  if (!response) throw new Error('No response from audience preview API');
  return response as CampaignAudiencePreview;
}

export async function previewCampaignDraftAudience(
  id: number,
  sample = 20,
): Promise<CampaignAudiencePreview> {
  const { getRequest } = await import('../../../services/apiService');
  const response = await getRequest(`/api/v1/contact/campaign/${id}/audience-preview?sample=${sample}`);
  return (response?.data ?? response) as CampaignAudiencePreview;
}

export async function deleteCampaignDraft(id: number): Promise<boolean> {
  const { deleteRequest } = await import('../../../services/apiService');
  const response = await deleteRequest(`/api/v1/contact/campaign/${id}`);
  return response !== null;
}

export async function cancelCampaign(id: number): Promise<Campaign> {
  const { postRequest } = await import('../../../services/apiService');
  const response = await postRequest(`/api/v1/contact/campaign/${id}/cancel`, {});
  if (!response) throw new Error('No response');
  return response as Campaign;
}

export async function pauseCampaign(id: number): Promise<Campaign> {
  const { postRequest } = await import('../../../services/apiService');
  const response = await postRequest(`/api/v1/contact/campaign/${id}/pause`, {});
  if (!response) throw new Error('No response');
  return response as Campaign;
}

export async function resumeCampaign(id: number): Promise<Campaign> {
  const { postRequest } = await import('../../../services/apiService');
  const response = await postRequest(`/api/v1/contact/campaign/${id}/resume`, {});
  if (!response) throw new Error('No response');
  return response as Campaign;
}

/**
 * Admin override that immediately runs one batch for the campaign,
 * bypassing the 5-min tick-based send window. Server still respects
 * warm-up daily cap, marketing-suppression checks, and per-send pacing.
 * Synchronous on the server (typically &lt;30 s) so the returned
 * campaign reflects the post-batch state.
 */
export async function runCampaignBatchNow(id: number): Promise<Campaign> {
  const { postRequest } = await import('../../../services/apiService');
  const response = await postRequest(`/api/v1/contact/campaign/${id}/run-batch-now`, {});
  if (!response) throw new Error('No response');
  return response as Campaign;
}

export async function getCampaignDetail(id: number): Promise<Campaign> {
  const { getRequest } = await import('../../../services/apiService');
  const response = await getRequest(`/api/v1/contact/campaign/${id}`);
  return response?.data ?? response;
}

export async function backfillCampaignEngagement(id: number): Promise<CampaignEngagementBackfillResponse> {
  const { postRequest } = await import('../../../services/apiService');
  const response = await postRequest(`/api/v1/admin/campaigns/${id}/backfill-engagement`, {});
  if (!response) throw new Error('No response from engagement backfill API');
  return response as CampaignEngagementBackfillResponse;
}

export type CampaignRecipientStatusFilter = 'ALL' | CampaignRecipient['status'];

/**
 * Recipient page. {@code status} pushes a server-side filter so the
 * loaded view always matches the campaign-wide breakdown — without it
 * the UI was filtering only whatever rows happened to be paginated in,
 * which silently hid "sent" rows on large campaigns.
 */
export async function getCampaignRecipients(
  id: number,
  page = 0,
  size = 50,
  status: CampaignRecipientStatusFilter = 'ALL',
): Promise<CampaignRecipient[]> {
  const { getRequest } = await import('../../../services/apiService');
  const qs = new URLSearchParams({ page: String(page), size: String(size) });
  if (status && status !== 'ALL') qs.set('status', status);
  const response = await getRequest(`/api/v1/contact/campaign/${id}/recipients?${qs.toString()}`);
  return response?.data ?? [];
}

export type SuppressCampaignRecipientResponse = CampaignRecipient & {
  campaign?: Campaign;
};

export async function suppressCampaignRecipient(
  campaignId: number,
  recipientId: number,
): Promise<SuppressCampaignRecipientResponse> {
  const { postRequest } = await import('../../../services/apiService');
  const response = await postRequest(`/api/v1/contact/campaign/${campaignId}/recipients/${recipientId}/suppress`, {});
  if (!response) throw new Error('No response from recipient suppression API');
  return response as SuppressCampaignRecipientResponse;
}

/**
 * Body shape for {@link renderEmailPreview}. Mirrors the campaign
 * launch payload so the same form state in the Start Campaign modal
 * can drive both — type any field empty/null to fall back to its
 * default (e.g. "Open BookieBrawler" CTA, generic recipient).
 */
export type RenderPreviewBody = {
  subject: string;
  bodyText: string;
  ctaText?: string;
  ctaPath?: string;
  preheader?: string;
  fromAddress?: string;
  /** Sample recipient for the greeting + per-user unsubscribe URL. */
  recipientEmail?: string;
};

/**
 * Server-rendered HTML preview of a campaign email. Returns the exact
 * HTML payload that would be handed to Resend at send time so the
 * admin can iframe it for a "what will actually go out" preview.
 * Throws when the backend rejects the input (e.g. malformed
 * fromAddress) so the modal can surface the validation error.
 */
export async function renderEmailPreview(body: RenderPreviewBody): Promise<string> {
  const { postRequest } = await import('../../../services/apiService');
  const response = await postRequest('/api/v1/contact/render-preview', body);
  if (!response || typeof response.html !== 'string') {
    throw new Error('Empty preview response');
  }
  return response.html as string;
}

/**
 * Fire-and-forget campaign test send. The campaign-specific endpoint
 * applies the same marketing-domain sender policy as a launched campaign,
 * including when From is blank or is only a first name.
 */
export async function sendTestCampaignEmail(
  body: RenderPreviewBody & { testAddress: string },
): Promise<ContactEmailResponse> {
  const request: ContactEmailRequest = {
    subject: body.subject,
    bodyText: body.bodyText,
    ctaText: body.ctaText,
    ctaPath: body.ctaPath,
    preheader: body.preheader,
    fromAddress: body.fromAddress,
    testOnly: true,
    testAddress: body.testAddress,
  } as ContactEmailRequest;
  const { postRequest } = await import('../../../services/apiService');
  const response = await postRequest('/api/v1/contact/campaign-test', request);
  if (!response) throw new Error('No response from campaign test email API');
  return response as ContactEmailResponse;
}
